// Admin API client — talks to FastAPI via the Vite proxy on /api/* (dev)
// or directly via VITE_API_BASE (production).
// The admin key is passed in x-admin-key header. The user types it in once
// at sign-in and we store it in localStorage (operator's machine only).

const ADMIN_KEY_STORAGE = 'datames_admin_key';

// In dev (Vite server with proxy), use the same-origin path so the
// proxy can rewrite /api/* to FastAPI. In production (Cloudflare Pages),
// the operator must set VITE_API_BASE to the Railway service URL.
const ADMIN_API_BASE_URL =
  import.meta.env.VITE_API_BASE ||
  (import.meta.env.DEV ? '/api' : '');

if (!ADMIN_API_BASE_URL && !import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.error(
    '[Datames Admin] VITE_API_BASE is not set. Set it in your Cloudflare Pages ' +
    'environment variables.'
  );
}

export function getAdminKey() {
  return localStorage.getItem(ADMIN_KEY_STORAGE) || '';
}

export function setAdminKey(key) {
  localStorage.setItem(ADMIN_KEY_STORAGE, key);
}

export function clearAdminKey() {
  localStorage.removeItem(ADMIN_KEY_STORAGE);
}

export async function adminFetch(path, options = {}) {
  const key = getAdminKey();
  if (!key) throw new Error('not_signed_in');
  const fullPath = path.startsWith('/') ? path : `/${path}`;
  // Auto-serialize JS objects to JSON for the body. fetch() doesn't do
  // this automatically — without it the body becomes the literal string
  // "[object Object]" which the server rejects as invalid JSON.
  let body = options.body;
  const headers = {
    'x-admin-key': key,
    ...(options.headers || {}),
  };
  if (body !== undefined && body !== null && typeof body !== 'string') {
    body = JSON.stringify(body);
    if (!('Content-Type' in headers)) headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${ADMIN_API_BASE_URL}${fullPath}`, { ...options, body, headers });
  if (!res.ok) {
    const body = await res.text();
    let parsed;
    try { parsed = JSON.parse(body); } catch { parsed = body; }
    if (res.status === 401) {
      clearAdminKey();
      throw Object.assign(new Error('unauthorized'), { status: 401 });
    }
    // Extract a human-readable message. FastAPI returns:
    //   { detail: "string" }  — simple case
    //   { detail: { code, message, raw } }  — our custom error shape
    //   { detail: [...] }  — validation errors
    let msg;
    if (typeof parsed?.detail === 'string') {
      msg = parsed.detail;
    } else if (parsed?.detail?.message) {
      msg = parsed.detail.message;
    } else if (parsed?.detail?.code) {
      msg = `${parsed.detail.code}: ${parsed.detail.message || ''}`;
    } else if (Array.isArray(parsed?.detail)) {
      msg = parsed.detail.map(e => e.msg || JSON.stringify(e)).join('; ');
    } else {
      msg = res.statusText;
    }
    const err = Object.assign(new Error(msg), {
      status: res.status,
      detail: parsed?.detail || body,
    });
    throw err;
  }
  return res.json();
}
